/**
 * What the staff routes answer, shared by the Worker that builds the answers and the admin app that
 * reads them. They are type aliases rather than interfaces because the Worker's `json()` takes a
 * JSON value, and only an alias is assignable to an index-signature type.
 *
 * Times are ISO 8601 UTC strings. Every staff change carries a reason, normalized by `staffReason`
 * on both sides so the form refuses exactly what the Worker would.
 */
import type { ClinicLink, ClinicRole, StaffRole } from "./access.ts";
import type { LicenceState, LicenceStatus } from "./clinic.ts";
import type { ExceptionKind, PharmacyIntegration } from "./fulfillment.ts";
import type {
  ClinicalReviewState,
  OrderState,
  PharmacyOrderState,
  SubscriptionState,
} from "./machines.ts";

/** The longest reason a staff member can give. Long enough for a ticket number and a sentence. */
export const STAFF_REASON_MAX = 500;

/** A reason with its edges trimmed, or null when it is empty or longer than STAFF_REASON_MAX. */
export function staffReason(input: string | null | undefined): string | null {
  const reason = (input ?? "").trim();

  return reason.length === 0 || reason.length > STAFF_REASON_MAX ? null : reason;
}

/**
 * Where a customer stands in onboarding, furthest step first. Labs covers upload and extraction.
 * Checkout covers an approved formula waiting to be paid, including a pending first invoice.
 */
export const FUNNEL_STAGES = ["labs", "questions", "reviewing", "checkout", "paid"] as const;

export type FunnelStage = (typeof FUNNEL_STAGES)[number];

export function isFunnelStage(value: string | null | undefined): value is FunnelStage {
  return value !== null && value !== undefined && FUNNEL_STAGES.some((stage) => stage === value);
}

/** The facts `funnelStage` needs. Each is the furthest of its kind for that customer. */
export type FunnelFacts = {
  readonly extracted: boolean;
  readonly intakeStarted: boolean;
  readonly formula: "none" | "draft" | "in_review" | "approved" | "declined" | "superseded";
  readonly order: "none" | "pending_payment" | "paid" | "released" | "cancelled" | "refunded";
};

/** The onboarding step a customer has reached, from labs through paid. */
export function funnelStage(facts: FunnelFacts): FunnelStage {
  if (facts.order === "paid" || facts.order === "released" || facts.order === "refunded")
    return "paid";

  if (facts.formula === "approved" || facts.order === "pending_payment") return "checkout";

  if (facts.formula === "in_review" || facts.formula === "declined") return "reviewing";

  if (facts.extracted || facts.intakeStarted) return "questions";

  return "labs";
}

export type AdminFunnelUser = {
  readonly id: string;
  readonly name: string;
  readonly email: string;
  readonly anonymous: boolean;
  readonly patientId: string;
  readonly stage: FunnelStage;
  readonly createdAt: string;
};

export type AdminFunnel = {
  readonly counts: { readonly [Stage in FunnelStage]: number };
  readonly users: readonly AdminFunnelUser[];
};

/**
 * One row on the audit log screen (ADM-050). Who, action, target, reason and when — never lab
 * values, intake answers or the `detail_json` blob, which is not for a person to read.
 */
export type AdminAuditLogRow = {
  readonly id: string;
  readonly action: string;
  readonly actorUserId: string | null;
  readonly actorEmail: string | null;
  readonly targetType: string;
  readonly targetId: string;
  /** A name or email for the target, when one is known. Never a clinical value. */
  readonly target: string | null;
  readonly reason: string | null;
  readonly createdAt: string;
};

export type AdminAuditLog = {
  readonly events: readonly AdminAuditLogRow[];
};

export type AdminUserRow = {
  readonly id: string;
  readonly name: string;
  readonly email: string;
  readonly staffRole: StaffRole | null;
  /** An account that started from an upload and has not verified an email yet. */
  readonly anonymous: boolean;
  readonly locked: boolean;
  readonly createdAt: string;
};

export type AdminPatientRow = {
  readonly id: string;
  readonly firstName: string | null;
  readonly lastName: string | null;
  /** Null until someone claims the record. */
  readonly userId: string | null;
  readonly email: string | null;
  readonly createdAt: string;
};

export type AdminOrganizationRow = {
  readonly id: string;
  readonly name: string;
  readonly slug: string | null;
  readonly members: number;
  readonly createdAt: string;
};

export type AdminSearchResult = {
  readonly query: string;
  readonly users: readonly AdminUserRow[];
  readonly patients: readonly AdminPatientRow[];
  readonly organizations: readonly AdminOrganizationRow[];
};

/** One audit_event about a record, newest first wherever a list of them is returned. */
export type AdminAuditRow = {
  readonly id: string;
  readonly action: string;
  readonly actorUserId: string | null;
  readonly actorEmail: string | null;
  readonly reason: string | null;
  /** Who the action was about when the target is not the record on screen, such as a linked patient. */
  readonly subject: string | null;
  readonly createdAt: string;
};

export type AdminUserDetail = {
  readonly user: AdminUserRow & {
    readonly emailVerified: boolean;
    readonly lockedAt: string | null;
    readonly lockReason: string | null;
  };
  readonly patientId: string | null;
  readonly memberships: readonly {
    readonly organizationId: string;
    readonly organizationName: string;
    readonly role: ClinicRole;
  }[];
  readonly activeSessions: number;
  readonly history: readonly AdminAuditRow[];
  /** Null when this account has never recorded a practitioner licence. */
  readonly licence: {
    readonly number: string;
    readonly state: LicenceState;
    readonly status: LicenceStatus;
    readonly verifiedByEmail: string | null;
    readonly verifiedAt: string | null;
  } | null;
};

export type AdminPatientLink = {
  readonly id: string;
  readonly patientId: string;
  readonly patientName: string | null;
  readonly status: ClinicLink["status"];
  readonly shareScope: "clinic_created" | "all";
  readonly practitionerName: string | null;
  readonly createdAt: string;
};

export type AdminOrganizationDetail = {
  readonly organization: AdminOrganizationRow;
  readonly members: readonly {
    readonly userId: string;
    readonly name: string;
    readonly email: string;
    readonly role: ClinicRole;
    readonly joinedAt: string;
    readonly licenceStatus: "none" | LicenceStatus;
  }[];
  readonly links: readonly AdminPatientLink[];
  /** Staff changes to this organization's patient links, including links moved away from it. */
  readonly history: readonly AdminAuditRow[];
};

/**
 * Sent with a `reason_required` refusal: whose record it is, so the prompt can name them. Nothing
 * clinical, and nothing staff search does not already show.
 */
export type AdminPatientIdentity = {
  readonly name: string | null;
  readonly email: string | null;
};

export type AdminPatientRecord = {
  readonly patient: {
    readonly id: string;
    readonly firstName: string | null;
    readonly lastName: string | null;
    readonly dateOfBirth: string | null;
    readonly sex: "female" | "male" | null;
    readonly userId: string | null;
    readonly email: string | null;
    readonly createdAt: string;
  };
  /** How this read was allowed: the case is in the reader's review queue, or they gave a reason. */
  readonly openedBy: "queue" | "reason";
  readonly links: readonly {
    readonly organizationId: string;
    readonly organizationName: string;
    readonly status: ClinicLink["status"];
  }[];
  readonly documents: readonly {
    readonly id: string;
    readonly filename: string;
    readonly status: "stored" | "extracting" | "extracted" | "failed";
    readonly collectedOn: string | null;
    readonly createdAt: string;
  }[];
  readonly observations: readonly {
    readonly id: string;
    readonly name: string;
    readonly valueNumeric: number | null;
    readonly valueText: string | null;
    readonly unit: string | null;
    readonly referenceText: string | null;
    readonly flag: "low" | "high" | "normal" | "abnormal" | null;
    readonly collectedOn: string | null;
  }[];
};

/** CLI-010, ADM-020: a decision within 24 hours of the review being created. */
export const REVIEW_TARGET_MS = 24 * 60 * 60 * 1000;

export const REVIEW_EDIT_REASONS = [
  "contraindication",
  "unnecessary",
  "dose",
  "missing_context",
  "manufacturing",
  "patient_preference",
  "cost",
  "other",
] as const;

export type ReviewEditReason = (typeof REVIEW_EDIT_REASONS)[number];

export const REVIEW_EDIT_CHANGES = ["added", "removed", "dose_changed"] as const;

export type ReviewEditChange = (typeof REVIEW_EDIT_CHANGES)[number];

export const DECLINE_REASONS = [
  "incomplete_information",
  "contraindication",
  "not_appropriate",
  "other",
] as const;

export type DeclineReason = (typeof DECLINE_REASONS)[number];

export function isReviewEditReason(value: string): value is ReviewEditReason {
  return REVIEW_EDIT_REASONS.some((reason) => reason === value);
}

export function isReviewEditChange(value: string): value is ReviewEditChange {
  return REVIEW_EDIT_CHANGES.some((change) => change === value);
}

export function isDeclineReason(value: string): value is DeclineReason {
  return DECLINE_REASONS.some((reason) => reason === value);
}

export type ReviewEditInput = {
  readonly ingredientId: string;
  readonly change: ReviewEditChange;
  readonly fromDose: number | null;
  readonly toDose: number | null;
  readonly reasonCode: ReviewEditReason;
  readonly note: string | null;
};

export type AdminReviewQueueRow = {
  readonly id: string;
  readonly formulaVersionId: string;
  readonly patientId: string;
  readonly patientName: string | null;
  readonly status: "waiting" | "in_progress";
  readonly createdAt: string;
  readonly waitingMs: number;
  readonly pastTarget: boolean;
};

export type AdminReviewQueue = {
  readonly reviews: readonly AdminReviewQueueRow[];
  readonly pastTarget: number;
};

export type AdminReviewIntake = {
  readonly age: number | null;
  readonly sex: "female" | "male" | null;
  readonly heightCm: number | null;
  readonly weightKg: number | null;
  readonly conditions: readonly string[];
  readonly medications: readonly string[];
  readonly supplements: readonly {
    readonly name: string;
    readonly dailyDose: number | null;
  }[];
  readonly allergies: readonly string[];
  readonly diet: "omnivore" | "pescatarian" | "vegetarian" | "vegan" | "other" | null;
  readonly exerciseDaysPerWeek: number | null;
  readonly exerciseType: "none" | "strength" | "endurance" | "mixed" | "other" | null;
  readonly goal: "general" | "energy" | "strength" | "weight" | "other" | null;
};

export type AdminReviewObservation = {
  readonly id: string;
  readonly name: string;
  readonly valueNumeric: number | null;
  readonly valueText: string | null;
  readonly unit: string | null;
  readonly referenceText: string | null;
  readonly flag: "low" | "high" | "normal" | "abnormal" | null;
  readonly collectedOn: string | null;
  readonly confidence: number;
  readonly sourcePage: number;
  readonly biomarkerCode: string | null;
};

export type AdminReviewComponent = {
  readonly ingredientId: string;
  readonly ingredientName: string;
  readonly dose: number;
  readonly unit: string;
  readonly separateAddon: boolean;
  readonly rationale: string;
};

export type AdminReviewFlag = {
  readonly name: string;
  readonly flag: "low" | "high" | "abnormal";
};

export type AdminReviewCatalogItem = {
  readonly id: string;
  readonly name: string;
  readonly unit: string;
};

export type AdminReviewViolation = {
  readonly kind: string;
  readonly severity: "block" | "warn";
  readonly ingredientIds: readonly string[];
  readonly ruleId: string | null;
  readonly note: string;
};

export type AdminReview = {
  readonly id: string;
  readonly status: ClinicalReviewState;
  readonly decision: "approved" | "edited" | "declined" | null;
  readonly formulaVersionId: string;
  readonly resultVersionId: string | null;
  readonly reviewerUserId: string | null;
  readonly patientId: string;
  readonly patientName: string | null;
  readonly createdAt: string;
  readonly waitingMs: number;
  readonly pastTarget: boolean;
  readonly intake: AdminReviewIntake;
  readonly observations: readonly AdminReviewObservation[];
  readonly components: readonly AdminReviewComponent[];
  readonly catalog: readonly AdminReviewCatalogItem[];
  readonly flags: readonly AdminReviewFlag[];
  readonly missingMarkers: readonly string[];
  readonly violations: readonly AdminReviewViolation[];
};

export type ReviewDecision = {
  readonly decision: "approved" | "edited" | "declined";
  readonly formulaVersionId: string;
  readonly resultVersionId: string | null;
};

/** Milliseconds waiting from `createdAt` until `endMs`, never negative. */
export function waitingMs(createdAt: string, endMs: number): number {
  const start = Date.parse(createdAt);

  return Number.isFinite(start) ? Math.max(0, endMs - start) : 0;
}

function listedEdit(edit: ReviewEditInput): ReviewEditInput | null {
  if (edit.ingredientId.trim() === "") return null;

  if (edit.change === "added" || edit.change === "dose_changed") {
    if (edit.toDose === null || !Number.isFinite(edit.toDose) || edit.toDose <= 0) return null;
  }

  return {
    ingredientId: edit.ingredientId,
    change: edit.change,
    fromDose: edit.fromDose !== null && Number.isFinite(edit.fromDose) ? edit.fromDose : null,
    toDose: edit.toDose !== null && Number.isFinite(edit.toDose) ? edit.toDose : null,
    reasonCode: edit.reasonCode,
    note: staffReason(edit.note),
  };
}

/**
 * Edits on an approve body. Missing or empty `edits` is an approve-as-is. Null when a listed edit
 * is missing a reason code, a dose, or an ingredient.
 */
export function parseReviewEdits(
  edits: readonly {
    readonly ingredientId?: string;
    readonly change?: string;
    readonly fromDose?: number | null;
    readonly toDose?: number | null;
    readonly reasonCode?: string;
    readonly note?: string | null;
  }[],
): readonly ReviewEditInput[] | null {
  const parsed: ReviewEditInput[] = [];

  for (const edit of edits) {
    const ingredientId = (edit.ingredientId ?? "").trim();
    const change = edit.change ?? "";
    const reasonCode = edit.reasonCode ?? "";

    if (!isReviewEditChange(change) || !isReviewEditReason(reasonCode)) return null;

    const listed = listedEdit({
      ingredientId,
      change,
      fromDose: edit.fromDose ?? null,
      toDose: edit.toDose ?? null,
      reasonCode,
      note: edit.note ?? null,
    });

    if (listed === null) return null;
    parsed.push(listed);
  }

  return parsed;
}

/** A decline body with a listed reason and a customer-facing note, or why it is refused. */
export function parseDecline(
  reason: string,
  note: string,
):
  | { readonly ok: true; readonly reason: DeclineReason; readonly note: string }
  | { readonly ok: false; readonly error: "reason_required" | "note_required" } {
  const trimmedReason = reason.trim();
  const trimmedNote = staffReason(note);

  if (!isDeclineReason(trimmedReason)) return { ok: false, error: "reason_required" };

  if (trimmedNote === null) return { ok: false, error: "note_required" };

  return { ok: true, reason: trimmedReason, note: trimmedNote };
}

/** The patient a job is about. Name and email only, the same facts staff search already shows. */
export type AdminJobPatient = {
  readonly id: string;
  readonly name: string | null;
  readonly email: string | null;
};

export type AdminExtractionJob = {
  readonly id: string;
  readonly documentId: string;
  readonly filename: string;
  readonly status: "running" | "succeeded" | "failed";
  readonly documentStatus: "stored" | "extracting" | "extracted" | "failed";
  readonly model: string;
  readonly promptVersion: string;
  readonly error: string | null;
  readonly observationCount: number;
  readonly retryable: boolean;
  readonly startedAt: string;
  readonly finishedAt: string | null;
  readonly patient: AdminJobPatient;
};

export type AdminDraftJob = {
  readonly id: string;
  readonly version: number;
  readonly status: "draft" | "in_review" | "approved" | "declined" | "superseded";
  readonly engineVersion: string;
  readonly ruleSetVersion: string;
  readonly componentCount: number;
  readonly observationCount: number;
  readonly intakeCount: number;
  readonly createdAt: string;
  readonly patient: AdminJobPatient;
};

/** What the next extraction and the next draft will use, read from the running Worker. */
export type AdminJobConfig = {
  readonly extractionModel: string;
  readonly promptVersion: string;
  readonly engineVersion: string;
  readonly ruleSetVersion: string;
};

export type AdminJobs = {
  readonly config: AdminJobConfig;
  readonly extractions: readonly AdminExtractionJob[];
  readonly drafts: readonly AdminDraftJob[];
};

/** 1, 2 or 3 months, the same pauses a customer can choose (ACC-021). */
export const PAUSE_MONTHS = [1, 2, 3] as const;

export type PauseMonths = (typeof PAUSE_MONTHS)[number];

/** The pause length from a form field, or null when it is not 1, 2 or 3. */
export function pauseMonths(input: string): PauseMonths | null {
  return PAUSE_MONTHS.find((months) => String(months) === input) ?? null;
}

/** The calendar date a pause of `months` starting at `fromIso` ends, as YYYY-MM-DD in UTC. */
export function pauseEndsOn(fromIso: string, months: PauseMonths): string {
  const from = new Date(fromIso);

  const end = new Date(
    Date.UTC(from.getUTCFullYear(), from.getUTCMonth() + months, from.getUTCDate()),
  );

  return end.toISOString().slice(0, 10);
}

export type AdminPharmacyOrderRow = {
  readonly id: string;
  readonly orderId: string;
  readonly state: PharmacyOrderState;
  readonly heldFrom: PharmacyOrderState | null;
  readonly externalId: string | null;
  readonly integration: PharmacyIntegration;
  readonly createdAt: string;
  readonly amountCents: number;
  readonly orderStatus: OrderState;
  readonly patientId: string;
  readonly userId: string | null;
  readonly customerName: string | null;
  readonly email: string | null;
  readonly openException: {
    readonly kind: ExceptionKind;
    readonly note: string | null;
  } | null;
  readonly openTask: {
    readonly id: string;
    readonly kind: "enter_order" | "cancel_order";
  } | null;
};

export type AdminPharmacyOrders = {
  readonly orders: readonly AdminPharmacyOrderRow[];
};

export type AdminFulfillmentEventRow = {
  readonly id: string;
  readonly state: PharmacyOrderState;
  readonly source: "webhook" | "poll" | "manual" | "file" | "submission";
  readonly note: string | null;
  readonly outcome: "applied" | "refused";
  readonly recordedByUserId: string | null;
  readonly occurredAt: string;
};

export type AdminFulfillmentExceptionRow = {
  readonly id: string;
  readonly kind: ExceptionKind;
  readonly owner: "lattice" | "pharmacy" | "customer";
  readonly note: string | null;
  readonly status: "open" | "resolved";
  readonly createdAt: string;
};

export type AdminOperationsTaskRow = {
  readonly id: string;
  readonly kind: "enter_order" | "cancel_order";
  readonly status: "open" | "done";
  readonly note: string | null;
  readonly createdAt: string;
};

export type AdminPharmacyOrderDetail = {
  readonly order: AdminPharmacyOrderRow;
  readonly nextStates: readonly PharmacyOrderState[];
  readonly events: readonly AdminFulfillmentEventRow[];
  readonly exceptions: readonly AdminFulfillmentExceptionRow[];
  readonly tasks: readonly AdminOperationsTaskRow[];
  readonly history: readonly AdminAuditRow[];
};

export type AdminChargeRow = {
  readonly id: string;
  readonly amountCents: number;
  readonly status: OrderState;
  readonly createdAt: string;
  readonly refundable: boolean;
};

export type AdminBilling = {
  readonly subscription: {
    readonly id: string;
    readonly status: SubscriptionState;
    readonly nextShipOn: string | null;
    readonly pausedUntil: string | null;
    readonly cancelledAt: string | null;
  } | null;
  readonly charges: readonly AdminChargeRow[];
  readonly history: readonly AdminAuditRow[];
};

export type ReactionSeverity = "mild" | "moderate" | "severe";

export type ReactionReportStatus = "open" | "in_review" | "resolved";

/** One unresolved reaction report (SUP-004). Description is the customer's words. */
export type AdminReactionReport = {
  readonly id: string;
  readonly patientId: string;
  readonly patientName: string | null;
  readonly formulaVersionId: string | null;
  readonly description: string;
  readonly severity: ReactionSeverity | null;
  readonly assignedTo: string | null;
  readonly assignedEmail: string | null;
  readonly status: ReactionReportStatus;
  readonly createdAt: string;
};

export type AdminReactionReportList = {
  readonly reports: readonly AdminReactionReport[];
};

/** What a customer gets back after filing a reaction report. */
export type ReactionReportAccepted = {
  readonly id: string;
};
